import { dateBeforeA } from '@src/utils/helpers';
import { QUESTION_TYPE } from '@components/data-driven-form/ddf.validation';
import NonPcnSuggest from '@src/app-pages/Forms/input-forms/NonPcnForm/NonPcnSuggest';

const SelfVerifySuggestMap = {
  11: NonPcnSuggest,
};

const onNumberKeyDown = (e) => {
  // do not allow exponent notation in number inputs
  if (e.key === 'e' || e.key === 'E') {
    e.preventDefault();
  }
};

const comparisonFn = {
  contains: (inputValue, triggerValue) => {
    return Array.isArray(triggerValue)
      ? triggerValue.includes(String(inputValue)?.toLowerCase())
      : String(triggerValue)?.toLowerCase().includes(String(inputValue)?.toLowerCase());
  },

  exists: (inputValue) =>
    inputValue !== undefined && inputValue !== null && inputValue !== '' && inputValue?.length > 0,

  '=': (inputValue, triggerValue) => String(inputValue).toLowerCase() === String(triggerValue).toLowerCase(),

  '!=': (inputValue, triggerValue) =>
    String(inputValue).toLowerCase() !== String(triggerValue).toLowerCase() &&
    inputValue !== undefined &&
    inputValue !== null &&
    inputValue !== '' &&
    inputValue?.length > 0,
};

const yesNoAnswerMap = {
  true: 'YES',
  false: 'NO',
};

const getComparisonValue = (typeId, value) => {
  if (typeId === QUESTION_TYPE.yesNo) {
    return yesNoAnswerMap[value];
  }
  if (typeId === QUESTION_TYPE.multiselect && Array.isArray(value)) {
    return value?.map((item) => item.value);
  }
  return value;
};

export const getEndContent = (inputValue, comparisonObjects, questionTypeId, reqTypeId) => {
  const SelfVerifySuggest = SelfVerifySuggestMap[reqTypeId];
  const endConditionMatch = comparisonObjects?.find((obj) => {
    const { comparison, value } = obj;
    const comparisonValue = getComparisonValue(questionTypeId, inputValue);
    return comparisonFn[comparison.toLowerCase()](comparisonValue, value);
  });
  return endConditionMatch ? (
    endConditionMatch.content
  ) : // If request is a self-verification tool and does not have an end condition match, default result to empty string
  reqTypeId === 11 ? (
    <SelfVerifySuggest result={''} />
  ) : null;
};

export const getFormContentVisibility = ({ formValues, parentThreads }) => {
  const areAnyParentThreadsVisible =
    parentThreads.length > 0
      ? parentThreads.some((thread) => {
          // Ensure that every question in the thread is visible
          const areAllParentsVisible = thread.every((parent) => {
            const parentKey = parent.parentName;
            const parentValue = formValues[parentKey];
            const parentComparisonValue = getComparisonValue(parent.parentTypeId, parentValue);
            const isParentVisible = comparisonFn[parent.comparison.toLowerCase()](
              parentComparisonValue,
              parent.triggerValue
            );

            // For question with AND conditions, check required question threads
            const requiredQuestionThreads = parent.requiredQuestionThreads || [];
            const areAnyRequiredThreadsVisible = requiredQuestionThreads.length
              ? requiredQuestionThreads.some((reqThread) => {
                  const areAllReqParentsVisible = reqThread.every((reqParent) => {
                    const reqParentKey = reqParent.parentName;
                    const reqParentValue = formValues[reqParentKey];
                    const reqParentComparisonValue = getComparisonValue(reqParent.parentTypeId, reqParentValue);
                    const isReqParentVisible = comparisonFn[reqParent.comparison.toLowerCase()](
                      reqParentComparisonValue,
                      reqParent.triggerValue
                    );

                    return isReqParentVisible;
                  });

                  return areAllReqParentsVisible;
                })
              : true;

            return isParentVisible && areAnyRequiredThreadsVisible;
          });

          return areAllParentsVisible;
        })
      : true;

  return areAnyParentThreadsVisible;
};

export const getOtherProps = ({
  allowsMultiselect,
  componentID,
  defaultValue,
  handleOnChange,
  isContactInput,
  isDateInput,
  isFileInput,
  isLongAnswerInput,
  isNumberInput,
  isOptionInput,
  isReadOnly,
  isShortAnswerInput,
  isTableInput,
  contactAnswerSet = null,
  formId,
  lists,
  onChangeProps,
  options,
  questionId,
  questionDocumentFiles,
  questionData,
}) => {
  const props = {};

  if (isOptionInput) {
    const inputOptions = options || lists;
    props.options = inputOptions?.map((option) => ({
      label: option.label || option.valueDesc,
      sortOrder: option.sortOrder,
      value: option.value ?? option.valueDesc,
    }));
    props.fullWidth = false;
    props.defaultValue = defaultValue ?? '';
  }

  if (isNumberInput) {
    props.type = 'number';
    props.onKeyDown = onNumberKeyDown;
    props.defaultValue = defaultValue;
  }

  if (isShortAnswerInput) {
    props.defaultValue = defaultValue;
    props.maxLength = 500;
  }

  if (isLongAnswerInput) {
    props.defaultValue = defaultValue;
  }

  if (isDateInput) {
    props.min = dateBeforeA;
  }

  if (allowsMultiselect) {
    props.fullWidth = false;
    props.handleChange = handleOnChange;
    props.multi = true;
  }

  if (isTableInput) {
    props.formId = formId;
    props.isReadOnly = isReadOnly;
    props.questionId = questionId;
  }

  if (isFileInput) {
    props.componentID = componentID;
    props.fileDownload = questionDocumentFiles;
    props.formId = formId;
    props.onChangeProps = onChangeProps;
    props.questionData = questionData;
  }

  if (isContactInput && contactAnswerSet) {
    props.contactType = questionData.contactType;
    props.dataKey = 'contactAnswers';
    props.formId = formId;
    props.initialContacts = contactAnswerSet.filter((contact) => contact.questionId === questionId);
    props.isReadOnly = isReadOnly;
    props.questionId = questionId;
    props.questionType = QUESTION_TYPE.contact;
  }

  return props;
};
